import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Loader2, Check, AlertTriangle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/Motion";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { api, errMsg } from "@/lib/api";

const LANGS = [["Arabic", "العربية"], ["English", "الإنجليزية"]];
const STYLE = {
  Sulafat: "دافئ", Achernar: "ناعم", Vindemiatrix: "لطيف", Leda: "شبابي", Aoede: "منعش", Despina: "سلس",
  Erinome: "واضح", Callirrhoe: "هادئ", Gacrux: "ناضج", Kore: "ثابت", Autonoe: "مشرق", Laomedeia: "مرح",
};

// Google error bodies come back as a Blob because previews are requested as audio.
const blobErr = async (e) => {
  try { return JSON.parse(await e?.response?.data?.text())?.detail || errMsg(e); } catch { return errMsg(e); }
};

const LangVoices = ({ lang, label, options, selected, onSaved }) => {
  const [busy, setBusy] = useState("");
  const audio = useRef(null);
  useEffect(() => () => { audio.current?.pause(); }, []);

  const preview = async (voice) => {
    setBusy(voice);
    try {
      const r = await api.post("/admin/voices/preview", { language: lang, voice }, { responseType: "blob" });
      audio.current?.pause();
      audio.current = new Audio(URL.createObjectURL(r.data));
      await audio.current.play();
    } catch (e) { toast.error(await blobErr(e)); } finally { setBusy(""); }
  };
  const choose = async (voice) => {
    try {
      await api.put("/admin/voices", { language: lang, voice });
      toast.success(`صار صوت ${label}: ${voice}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="bg-white border border-[#E5DCCB] p-5 sm:p-6" data-testid={`voices-${lang}`}>
      <h2 className="font-display text-2xl font-bold mb-1">{label}</h2>
      <p className="text-sm text-stone-600 mb-4">الصوت الحالي: <span className="font-semibold" data-testid={`voices-${lang}-selected`}>{selected}</span></p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {options.map((v) => (
          <div key={v} className={`flex items-center gap-2 border p-2 ${v === selected ? "border-[#8B3A2B] bg-[#8B3A2B]/5" : "border-[#E5DCCB]"}`}>
            <button onClick={() => preview(v)} disabled={!!busy} aria-label={`تجربة ${v}`} data-testid={`voice-preview-${lang}-${v}`}
              className="w-9 h-9 shrink-0 rounded-full bg-[#1C1917] text-white grid place-items-center disabled:opacity-40">
              {busy === v ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            </button>
            <span className="flex-1 min-w-0 text-sm"><span className="font-semibold" dir="ltr">{v}</span> · {STYLE[v]}</span>
            {v === selected ? <Check className="w-5 h-5 text-[#8B3A2B]" data-testid={`voice-selected-${lang}-${v}`} /> : (
              <button onClick={() => choose(v)} data-testid={`voice-choose-${lang}-${v}`}
                className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#8B3A2B] text-[#8B3A2B] hover:bg-[#8B3A2B] hover:text-white transition-colors">اختيار</button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default function AdminVoices() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/voices").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const options = Array.isArray(data?.options) ? data.options : [];

  return (
    <div className="min-h-screen bg-[#FAF7F2]" dir="rtl" data-testid="admin-voices-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="mb-8">
          <p className="text-sm font-semibold text-[#B45309] mb-2">لوحة الإدارة</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold"><MaskLines lines={["أصوات القراءة"]} delay={0.1} /></h1>
          <p className="mt-3 text-stone-600 max-w-2xl">اضغط زر التشغيل لسماع جملة قصيرة بكل صوت، ثم اختر أجملها لكل لغة. بعد الاختيار تُجهَّز صفحات الكتب بالصوت الجديد تلقائياً.</p>
        </div>
        <AdminTabs />
        {data && !data.google_ready && (
          <p className="flex gap-2 items-start text-sm bg-amber-50 border border-amber-200 text-amber-900 p-4 mb-6" data-testid="voices-google-warning">
            <AlertTriangle className="w-5 h-5 shrink-0" /> أصوات Google غير متاحة الآن (غالباً لأن رصيد حساب Google منتهٍ)، فيُستخدم الصوت الاحتياطي حتى يُشحن الرصيد.
          </p>
        )}
        <div className="space-y-6">
          {LANGS.map(([lang, label]) => (
            <LangVoices key={lang} lang={lang} label={label} options={options} selected={data?.selected?.[lang] ?? "Sulafat"} onSaved={load} />
          ))}
        </div>
      </div>
    </div>
  );
}
